// Authentication utility functions for admin panels
import { apiUrl } from './config';
import logger from './logger';

// Token storage keys
const TOKEN_KEY = 'admin_token';
const USERNAME_KEY = 'admin_username';

/**
 * Login with username and password
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const login = async (username, password) => {
  try {
    const loginUrl = apiUrl('auth/login');
    
    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });

    if (response.ok) {
      const data = await response.json();
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      sessionStorage.setItem(USERNAME_KEY, data.username);
      return { success: true };
    } else {
      const error = await response.json();
      return { success: false, error: error.detail || 'Login failed' };
    }
  } catch (error) {
    logger.error('Login error:', error.message);
    return { success: false, error: `Network error: ${error.message}` };
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!sessionStorage.getItem(TOKEN_KEY);
};

/**
 * Get the stored auth token
 * @returns {string|null}
 */
export const getToken = () => {
  return sessionStorage.getItem(TOKEN_KEY);
};

/**
 * Get the stored username
 * @returns {string|null}
 */
export const getUsername = () => {
  return sessionStorage.getItem(USERNAME_KEY);
};

/**
 * Logout - clear stored credentials
 */
export const logout = () => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USERNAME_KEY);
};

/**
 * Verify if the current token is still valid
 * @returns {Promise<boolean>}
 */
export const verifyToken = async () => {
  const token = getToken();
  if (!token) return false;

  try {
    const response = await fetch(apiUrl('auth/verify'), {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.ok;
  } catch (error) {
    logger.error('Token verification error:', error);
    return false;
  }
};

/**
 * Get headers with authentication token
 * @returns {Object}
 */
export const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
};

/**
 * Change admin password
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const changePassword = async (currentPassword, newPassword) => {
  const token = getToken();
  if (!token) return { success: false, error: 'Not authenticated' };

  try {
    const response = await fetch(apiUrl('auth/change-password'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ 
        current_password: currentPassword, 
        new_password: newPassword 
      })
    });

    if (response.ok) {
      return { success: true };
    } else {
      const error = await response.json();
      return { success: false, error: error.detail || 'Failed to change password' };
    }
  } catch (error) {
    logger.error('Change password error:', error);
    return { success: false, error: 'Network error. Please try again.' };
  }
};
